<template>
  <!--
    Nothing renders when nothing resolves. The support keys carry Twine's desk as
    their registered default, so this guard is for a null prop on the landlord
    domain rather than for a tenant with no contact — that tenant cannot exist.
  -->
  <section
    v-if="hasContact"
    :class="[
      'flex gap-x-6 gap-y-4 min-w-0',
      compact
        ? 'flex-col items-stretch'
        : 'bg-surface-0 rounded-xl shadow-sm p-4 sm:px-5 flex-wrap items-center justify-between'
    ]"
  >
    <div class="flex flex-row items-center gap-3 min-w-0">
      <img
        v-if="logoUrl"
        :alt="`${name} logo`"
        :src="logoUrl"
        class="h-11 w-11 shrink-0 rounded-xl border border-surface-200 bg-surface-0 object-contain p-1"
      />
      <div
        v-else
        class="h-11 w-11 shrink-0 rounded-xl bg-primary-100 text-primary-700 grid place-items-center font-semibold text-sm"
      >
        {{ initials }}
      </div>

      <div class="flex flex-col min-w-0">
        <span class="text-surface-500 text-xs uppercase tracking-wider">Your support</span>
        <span class="text-surface-900 font-semibold text-lg leading-snug truncate">
          {{ name }}
        </span>
      </div>
    </div>

    <!--
      In the partner's own order, on the href the CRM built. The first is filled:
      it is the channel they want tried first, and it keeps its value so somebody
      dialling from another handset can read it.
    -->
    <div :class="['flex gap-2 min-w-0', compact ? 'flex-col items-stretch' : 'flex-row flex-wrap items-center']">
      <!-- eslint-disable-next-line vue/no-restricted-html-elements -- tel:, mailto: and a partner's portal all leave the app, so there is nothing for Inertia to route -->
      <a
        v-for="(method, index) in methods"
        :key="`${method.channel}-${method.value}`"
        :class="[
          'inline-flex items-center gap-2 min-h-10 px-3 rounded-lg border text-sm min-w-0 max-w-full transition-colors',
          index === 0
            ? 'bg-primary-600 border-primary-600 text-surface-0 font-medium hover:bg-primary-700 hover:border-primary-700 pr-2'
            : 'bg-surface-0 border-surface-200 text-surface-900 hover:bg-surface-50'
        ]"
        :href="method.href"
        :rel="isExternal(method.href) ? 'noopener noreferrer' : undefined"
        :target="isExternal(method.href) ? '_blank' : undefined"
        :title="channelLabel(method)"
      >
        <TwnSupportChannelGlyph
          :channel="method.channel"
          :class="index === 0 ? 'text-surface-0/90' : 'text-surface-500'"
        />
        <span class="truncate tabular-nums">{{ method.value }}</span>
        <span
          v-if="method.label"
          :class="[
            'shrink-0 rounded-md px-1.5 py-0.5 text-[0.62rem] uppercase tracking-wide',
            index === 0 ? 'bg-surface-0/20 text-surface-0' : 'bg-surface-100 text-surface-500'
          ]"
        >
          {{ method.label }}
        </span>
      </a>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { TwnSupportContactCardProps } from '@/domains/tenant/components/molecules/twn-support-contact-card/TwnSupportContactCard.types'
import type { SupportContactMethodModel } from '@/domains/tenant/types/SupportContactModel.types'

import TwnSupportChannelGlyph from '@/domains/tenant/components/atoms/twn-support-channel-glyph/TwnSupportChannelGlyph.vue'
import { SUPPORT_CHANNEL_LABELS } from '@/domains/tenant/types/consts/SupportChannel.consts'

const props = withDefaults(defineProps<TwnSupportContactCardProps>(), { compact: false })

const name = computed((): string => props.supportContact?.name ?? 'Support')

// The template reads these rather than the prop, so a null tenant context
// narrows once here instead of at every binding.
const methods = computed((): SupportContactMethodModel[] => props.supportContact?.methods ?? [])

const logoUrl = computed((): string | null => props.supportContact?.logo_url ?? null)

const hasContact = computed(
  (): boolean => Boolean(props.supportContact?.name) || Boolean(props.supportContact?.methods.length)
)

const initials = computed((): string =>
  name.value
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase() ?? '')
    .join('')
)

const channelLabel = (method: SupportContactMethodModel): string =>
  SUPPORT_CHANNEL_LABELS[method.channel] ?? method.channel

// A portal opens in a new tab; a dialler or a mail client replaces nothing.
const isExternal = (href: string): boolean => href.startsWith('http')
</script>
